import { Fragment, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { MESES_ABREV, formatCurrency, formatPercent } from "../../utils/orcamento";
import { thStyle, tdStyle } from "./DreGerencialView";
import { EMPRESAS_DRE } from "../../constants/dreEmpresa";

// DRE por Empresa: abre o DRE Gerencial (consolidado) por empresa jurídica (GT/CIGTA/CRIA).
// Faturamento Bruto (Backlog) e Impostos são "dado real" (o segundo, calculado pela cascata de
// imposto — ver backend/services/dre_empresa_svc.py); as demais linhas de custo/despesa ainda
// não são apuradas por empresa nos módulos de origem, então usam o percentual configurável em
// "Premissas · Rateio entre Empresas" (grupo Premissas) — cada uma exibe um selo verde
// ("dado real") ou laranja ("rateio X%").
// Renderer de linha próprio (não reaproveita LinhaRow de DreGerencialView.js) para não arriscar
// regressão nas telas de grupo — só thStyle/tdStyle são compartilhados.

const PADDING_POR_NIVEL = [10, 16, 32, 48];

const BG_POR_TIPO = (C) => ({
  grupo: C.categoriaBg,
  subgrupo: C.subcategoriaBg,
  conta: "transparent",
  derivado: C.bgActive,
  margem: C.margemBg,
});

function Selo({ linha, C }) {
  if (!linha.origem_dado) return null;
  const real = linha.origem_dado === "real";
  return (
    <span
      style={{
        display: "inline-block", fontSize: 9.5, fontWeight: 700, borderRadius: 999,
        padding: "1px 7px", marginLeft: 8, whiteSpace: "nowrap",
        background: real ? `${C.success}22` : "#F5972222",
        color: real ? C.success : "#F59722",
        border: `1px solid ${real ? C.success : "#F59722"}`,
      }}
    >
      {real ? "dado real" : `rateio ${Number(linha.rateio_pct).toFixed(0)}%`}
    </span>
  );
}

function EmpresaLinhaRow({ linha, nivel, colapsados, toggle, C }) {
  const temFilhos = Array.isArray(linha.filhos) && linha.filhos.length > 0;
  const colapsada = colapsados.has(linha.id);
  const bg = BG_POR_TIPO(C)[linha.tipo] || "transparent";
  const negrito = linha.tipo === "grupo" || linha.tipo === "derivado" || linha.tipo === "margem";
  const italico = linha.tipo === "margem";
  const valorFmt = (v) => (linha.unidade === "pct" ? formatPercent(v) : formatCurrency(v));

  return (
    <Fragment>
      <tr
        onClick={temFilhos ? () => toggle(linha.id) : undefined}
        style={{
          background: bg, fontWeight: negrito ? 700 : 400, fontStyle: italico ? "italic" : "normal",
          cursor: temFilhos ? "pointer" : "default",
          borderTop: linha.tipo === "derivado" ? `2px solid ${C.border}` : undefined,
        }}
      >
        <td style={tdStyle(C, { textAlign: "left" })}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, paddingLeft: PADDING_POR_NIVEL[nivel] || 10 }}>
            {temFilhos ? (colapsada ? <ChevronRight size={13} /> : <ChevronDown size={13} />) : null}
            <span>{linha.label}</span>
            <Selo linha={linha} C={C} />
          </div>
        </td>
        {linha.meses.map((m) => (
          <td key={m.mes} style={tdStyle(C)}>{valorFmt(m.planejado)}</td>
        ))}
        <td style={tdStyle(C, { fontWeight: 700 })}>{valorFmt(linha.total.planejado)}</td>
      </tr>
      {temFilhos && !colapsada && linha.filhos.map((filho) => (
        <EmpresaLinhaRow key={filho.id} linha={filho} nivel={nivel + 1} colapsados={colapsados} toggle={toggle} C={C} />
      ))}
    </Fragment>
  );
}

function coletarIdsComFilhos(linhas) {
  const ids = [];
  for (const linha of linhas || []) {
    if (Array.isArray(linha.filhos) && linha.filhos.length > 0) {
      ids.push(linha.id);
      ids.push(...coletarIdsComFilhos(linha.filhos));
    }
  }
  return ids;
}

function KpiCard({ label, valor, sublinha, C }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 16px", minWidth: 170, flex: 1 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{valor}</div>
      {sublinha && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sublinha}</div>}
    </div>
  );
}

export default function DreEmpresaView({ data, ano, C }) {
  const empresas = useMemo(() => data?.empresas || [], [data]);
  const [empresaSelecionada, setEmpresaSelecionada] = useState(EMPRESAS_DRE[0]);
  const empresaAtual = useMemo(
    () => empresas.find((e) => e.empresa === empresaSelecionada) || empresas[0] || null,
    [empresas, empresaSelecionada]
  );

  const linhas = useMemo(() => empresaAtual?.linhas || [], [empresaAtual]);
  const [colapsados, setColapsados] = useState(() => new Set());
  useEffect(() => {
    setColapsados(new Set(coletarIdsComFilhos(linhas)));
  }, [empresaAtual, linhas]);

  function toggle(id) {
    setColapsados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const idsComFilhos = useMemo(() => coletarIdsComFilhos(linhas), [linhas]);
  const prejuizoFiscal = useMemo(() => {
    const linhaImpostos = linhas.find((l) => l.id === "impostos");
    return linhaImpostos?.prejuizo_fiscal || null;
  }, [linhas]);

  if (!empresaAtual) return null;

  const kpis = empresaAtual.kpis;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Comparativo entre empresas */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle(C, { textAlign: "left" })}>Empresa</th>
                <th style={thStyle(C)}>Faturamento Bruto</th>
                <th style={thStyle(C)}>Receita Líquida</th>
                <th style={thStyle(C)}>Lucro Bruto</th>
                <th style={thStyle(C)}>EBITDA</th>
                <th style={thStyle(C)}>LAIR</th>
                <th style={thStyle(C)}>Resultado Líquido</th>
              </tr>
            </thead>
            <tbody>
              {empresas.map((emp) => (
                <tr
                  key={emp.empresa}
                  onClick={() => setEmpresaSelecionada(emp.empresa)}
                  style={{
                    background: emp.empresa === empresaAtual.empresa ? C.bgActive : "transparent",
                    cursor: "pointer", fontWeight: emp.empresa === empresaAtual.empresa ? 700 : 400,
                  }}
                >
                  <td style={tdStyle(C, { textAlign: "left" })}>{emp.empresa}</td>
                  <td style={tdStyle(C)}>{formatCurrency(emp.kpis.faturamento_bruto.total.planejado)}</td>
                  <td style={tdStyle(C)}>{formatCurrency(emp.kpis.receita_liquida.total.planejado)}</td>
                  <td style={tdStyle(C)}>{formatCurrency(emp.kpis.lucro_bruto.total.planejado)}</td>
                  <td style={tdStyle(C)}>{formatCurrency(emp.kpis.ebitda.total.planejado)}</td>
                  <td style={tdStyle(C)}>{formatCurrency(emp.kpis.lair.total.planejado)}</td>
                  <td style={tdStyle(C, { fontWeight: 700 })}>{formatCurrency(emp.kpis.resultado_liquido.total.planejado)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Seletor de empresa */}
      <div style={{ display: "flex", gap: 8 }}>
        {EMPRESAS_DRE.map((emp) => (
          <button
            key={emp} type="button" onClick={() => setEmpresaSelecionada(emp)}
            style={{
              padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer",
              border: `1px solid ${emp === empresaAtual.empresa ? C.brand : C.border}`,
              background: emp === empresaAtual.empresa ? C.brand : "transparent",
              color: emp === empresaAtual.empresa ? "#fff" : C.text,
            }}
          >
            {emp}
          </button>
        ))}
      </div>

      {/* KPIs da empresa selecionada */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <KpiCard label="Faturamento Bruto" valor={formatCurrency(kpis.faturamento_bruto.total.planejado)} C={C} />
        <KpiCard label="Receita Líquida" valor={formatCurrency(kpis.receita_liquida.total.planejado)} C={C} />
        <KpiCard label="Lucro Bruto" valor={formatCurrency(kpis.lucro_bruto.total.planejado)} C={C} />
        <KpiCard label="EBITDA" valor={formatCurrency(kpis.ebitda.total.planejado)} C={C} />
        <KpiCard label="LAIR" valor={formatCurrency(kpis.lair.total.planejado)} C={C} />
        <KpiCard
          label="Resultado Líquido" valor={formatCurrency(kpis.resultado_liquido.total.planejado)}
          sublinha={prejuizoFiscal ? `Saldo Prejuízo Fiscal: ${formatCurrency(prejuizoFiscal.saldo_final[11])}` : undefined}
          C={C}
        />
      </div>

      {/* Botão expandir/recolher */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={() => setColapsados(colapsados.size > 0 ? new Set() : new Set(idsComFilhos))}
          style={{
            background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8,
            padding: "6px 12px", fontSize: 11.5, fontWeight: 600, color: C.text, cursor: "pointer",
          }}
        >
          {colapsados.size > 0 ? "Expandir tudo" : "Recolher tudo"}
        </button>
      </div>

      {/* Tabela de DRE da empresa selecionada */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1400 }}>
            <thead>
              <tr>
                <th style={thStyle(C, { textAlign: "left" })}>{empresaAtual.empresa} · {ano}</th>
                {MESES_ABREV.map((m) => <th key={m} style={thStyle(C, { textAlign: "center" })}>{m}</th>)}
                <th style={thStyle(C, { textAlign: "center" })}>Total</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((linha) => (
                <EmpresaLinhaRow key={linha.id} linha={linha} nivel={0} colapsados={colapsados} toggle={toggle} C={C} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
